import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CircleDot, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  Users,
  Award,
  AlertTriangle,
  Wind,
  Eye,
  Radio
} from "lucide-react";

const courseDetails = {
  duration: "16 שעות",
  certification: "תעודה מוכרת",
  participants: "עד 12 משתתפים",
  practical: "תרגול מעשי"
};

const courseContent = [
  {
    title: "הגדרות ומושגים",
    topics: [
      "מהו חלל מוקף",
      "סוגי חללים מוקפים",
      "סיכונים ייחודיים",
      "חקיקה ותקנות"
    ]
  },
  {
    title: "סיכונים באטמוספירה",
    topics: [
      "מחסור בחמצן",
      "גזים רעילים ודליקים",
      "ניטור אטמוספירי",
      "אוורור וסילוק סיכונים"
    ]
  },
  {
    title: "נהלי כניסה",
    topics: [
      "היתר כניסה לחלל מוקף",
      "בדיקות מקדימות",
      "ציוד נדרש",
      "תיאום והכנה"
    ]
  },
  {
    title: "חירום והצלה",
    topics: [
      "זיהוי מצבי חירום",
      "נהלי הצלה",
      "ציוד הצלה",
      "עזרה ראשונה"
    ]
  }
];

const confinedSpaceTypes = [
  { name: "מכלים וטנקים", example: "מכלי דלק, מיכלי אחסון" },
  { name: "בורות וצינורות", example: "תאי ביוב, צינורות גדולים" },
  { name: "מרתפים וחדרים", example: "חדרי משאבות, מרתפים ללא אוורור" },
  { name: "סילואים", example: "מגדלי אחסון, ממגורות" },
  { name: "תעלות ותאים", example: "תעלות כבלים, תאי בקרה" }
];

const equipment = [
  { icon: Wind, name: "מכשיר בדיקת אטמוספירה", use: "מדידת רמות חמצן וגזים" },
  { icon: Radio, name: "מערכת קשר", use: "תקשורת עם הצופה" },
  { icon: Eye, name: "תאורה מתאימה", use: "תאורה בטוחה לסביבה" },
  { icon: AlertTriangle, name: "ציוד נשימה", use: "מסכות ומכשירי נשימה" }
];

const faqs = [
  {
    question: "מי חייב לעבור הדרכת חללים מוקפים?",
    answer: "כל עובד שנכנס לחלל מוקף או משמש כצופה (Attendant) חייב לעבור הדרכה. גם מפקחים ומנהלים הקשורים לעבודה בחללים מוקפים צריכים להכיר את הנושא."
  },
  {
    question: "מה ההבדל בין נכנס לבין צופה?",
    answer: "הנכנס הוא העובד שנכנס פיזית לחלל המוקף. הצופה נשאר בפתח החלל, שומר על קשר עם הנכנס, ומוכן להזעיק עזרה במקרה חירום. שניהם חייבים בהדרכה."
  },
  {
    question: "האם יש תרגול מעשי בקורס?",
    answer: "כן, הקורס כולל תרגול מעשי עם ציוד בדיקה אטמוספרית, ציוד הגנה, ותרגילי כניסה והצלה בסביבה מבוקרת."
  },
  {
    question: "כמה זמן התעודה תקפה?",
    answer: "התעודה תקפה לשנתיים. מומלץ לעבור הדרכת רענון שנתית, ובמיוחד כאשר משתנים התהליכים או הציוד בארגון."
  }
];

export default function ConfinedSpaces() {
  return (
    <Layout>
      <Helmet>
        <title>הדרכת עבודה בחללים מוקפים | Adam Safety</title>
        <meta name="description" content="הדרכת עבודה בחללים מוקפים - מכלים, בורות, תעלות. כניסה בטוחה, ניטור אטמוספרי, נהלי חירום. הדרכה תיאורטית ומעשית עם תעודה." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <CircleDot className="w-5 h-5" />
              <span className="text-sm font-medium">הדרכת בטיחות</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              הדרכת עבודה בחללים מוקפים
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              הדרכה מקיפה לעבודה בטוחה בחללים מוקפים - 
              מכלים, בורות, תעלות ומרתפים
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.certification}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.participants}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמן הדרכה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Confined Space */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              מהו חלל מוקף?
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              חלל מוקף הוא מקום סגור או חצי סגור, עם פתחי כניסה ויציאה מוגבלים, 
              שלא תוכנן לשהייה רציפה של אנשים ובו עלולים להיות סיכונים אטמוספריים
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {confinedSpaceTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-card px-5 py-4 rounded-2xl border border-border shadow-sm"
              >
                <h3 className="font-bold text-foreground mb-1">{type.name}</h3>
                <p className="text-sm text-muted-foreground">{type.example}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              תכנית ההדרכה
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {courseContent.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <h3 className="text-lg font-bold text-foreground mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{topic}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              ציוד נדרש
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              הציוד שנלמד להשתמש בו במהלך ההדרכה
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {equipment.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground text-center"
              >
                <item.icon className="w-10 h-10 text-accent mx-auto mb-3" />
                <h3 className="font-bold mb-2">{item.name}</h3>
                <p className="text-primary-foreground/80 text-sm">{item.use}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Danger Warning */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto bg-red-50 dark:bg-red-900/20 rounded-3xl p-8 border border-red-200 dark:border-red-800"
          >
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
              <h2 className="text-2xl font-bold text-red-700 dark:text-red-400">סכנה!</h2>
            </div>
            <p className="text-red-800 dark:text-red-200 mb-4">
              עבודה בחללים מוקפים היא מהמסוכנות ביותר בעולם העבודה. 
              רוב התאונות הקטלניות בחללים מוקפים קורות דווקא בניסיונות הצלה לא מקצועיים.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <CheckCircle2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                לעולם אל תיכנס לחלל מוקף ללא הכשרה
              </li>
              <li className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <CheckCircle2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                תמיד בצע בדיקת אטמוספירה לפני הכניסה
              </li>
              <li className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <CheckCircle2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                וודא שיש צופה בחוץ לאורך כל הזמן
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              עובדים בחללים מוקפים?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              וודאו שהעובדים שלכם מוכשרים לעבודה בטוחה
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמנת הדרכה
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  הדרכות נוספות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}